/**
 * @format
 */
import {
   AfterViewInit,
   Component,
   ElementRef,
   Inject,
   Input,
   OnDestroy,
   OnInit,
   ViewChild,
   ViewEncapsulation,
} from "@angular/core";
import { fromEvent, Observable, Subscription } from "rxjs";
import { Store } from "@ngrx/store";
import {
   AlignmentType,
   ContentAndModelId,
   DropDownFont,
   DropDownFontItem,
   FontChangeType,
   FontStyleArgument,
   OriginalAndStyledValue,
} from "./types/editor-types";
import { EditorService } from "./services/editor.service";
import { EditorOptions } from "./types/editor-options";
import { NG_EDITOR_CONF } from "./tokens/ng-editor-config.token";
import { Dropdown } from "primeng/dropdown";
import { EditorSelectors } from "./state/editor.selectors";
import { EditorActions } from "./state/editor.actions";
import { filter, throttleTime, map } from "rxjs/operators";
import { PreviewEditTemplateActions } from "../preview-edit-template/state/preview-edit-template.actions";
import { ContenteditableDirective } from "../core/content-editable/contenteditable.directive";
import { document } from "ngx-bootstrap/utils";
import { StylesService } from "./services/styles.service";
import { LinkToolService } from "./services/link-tool.service";
import { AlignmentToolService } from "./services/alignment-tool.service";
import { CustomKeyboardEvent } from "../core/content-editable/types";
import { FontToolService } from "./services/font-tool.service";
import { UnderlineToolService } from "./services/underline-tool.service";
import { BoldToolService } from "./services/bold-tool.service";
import Timeout = NodeJS.Timeout;
import { PreviewEditTemplateSelectors } from "../preview-edit-template/state/preview-edit-template.selectors";

@Component({
   selector: "ng-editor",
   templateUrl: "./ng-editor.component.html",
   styleUrls: ["ng-editor.component.css"],
   encapsulation: ViewEncapsulation.None,
})
export class NgEditorComponent implements OnInit, OnDestroy, AfterViewInit {
   @Input() isDisable: boolean = false;
   @Input() templateUpdateModel;
   @ViewChild("container", { static: true }) container: ElementRef;
   @ViewChild("content", { static: true }) content: ElementRef;
   @ViewChild("toolbar", { static: true }) toolbar: ElementRef;
   @ViewChild("ddFont") fontDropDown: Dropdown;

   fontSource = [
      { name: "Montserrat", value: "Montserrat", id: 1 },
      { name: "Arial", value: "Arial", id: 2 },
      { name: "Arial Black", value: "Arial Black", id: 3 },
      { name: "Open Sans", value: "Open Sans", id: 4 },
      { name: "Helvetica", value: "Helvetica", id: 5 },
      { name: "Time New Roman", value: "Times", id: 6 },
   ];

   fontSize = [
      { name: "72", value: "72px" },
      { name: "60", value: "60px" },
      { name: "48", value: "48px" },
      { name: "36", value: "36px" },
      { name: "30", value: "30px" },
      { name: "28", value: "28px" },
      { name: "26", value: "26px" },
      { name: "24", value: "24px" },
      { name: "22", value: "22px" },
      { name: "20", value: "20px" },
      { name: "19", value: "19px" },
      { name: "18", value: "18px" },
      { name: "17", value: "17px" },
      { name: "16", value: "16px" },
      { name: "15", value: "15px" },
      { name: "14", value: "14px" },
      { name: "13", value: "13px" },
      { name: "12", value: "12px" },
      { name: "11", value: "11px" },
      { name: "10", value: "10px" },
      { name: "9", value: "9px" },
   ];
   selectedFont: DropDownFontItem;
   selectedFontSize: DropDownFont;
   selectedFontColor: string;
   selectedStylesSubject$ = this.stylesService.selectedStylesSubject$;
   isBold = false;
   isUnderline = false;
   textAlignment: string;
   canUndo$: Observable<boolean>;
   canRedo$: Observable<boolean>;
   getPresentAfterUndoRedo$: Observable<ContentAndModelId | null>;
   windowSelectionTimestamp$: Observable<number>;
   pickedColor: string;

   private _subscription: Subscription;
   private _previousTemplateVariableId: string = "";
   private _delayTimeOut: Timeout;

   constructor(
      @Inject(NG_EDITOR_CONF) private defaultConfig: EditorOptions,
      private store: Store,
      private editorSelectors: EditorSelectors,
      private editorService: EditorService,
      private stylesService: StylesService,
      private contenteditableDirective: ContenteditableDirective,
      private linkToolService: LinkToolService,
      private alignmentToolService: AlignmentToolService,
      private fontToolService: FontToolService,
      private underlineToolService: UnderlineToolService,
      private boldToolService: BoldToolService,
      private previewEditTemplateSelector: PreviewEditTemplateSelectors
   ) {
      this._subscription = new Subscription();
      this.canUndo$ = this.editorSelectors.canUndo();
      this.canRedo$ = this.editorSelectors.canRedo();
      this.getPresentAfterUndoRedo$ = this.editorSelectors.getPresent();
      this.windowSelectionTimestamp$ = this.editorSelectors.getWindowSelectionTimestamp();
   }

   ngOnInit(): void {
      this.selectedFontColor = "#000000";
      this.selectedStylesChangesSubscription();
      this.getPresentAfterUndoRedoSubscription();
      this.windowSelectionUpdateSubscription();
      this.templateUpdateModelChangesSubscription();
   }

   ngOnDestroy(): void {
      this.resetChanges();
      this._subscription.unsubscribe();
   }

   ngAfterViewInit(): void {
      if (this.isDisable) {
         return;
      }
      this.toolbarClickEventListenerSubscription();
      this.contentClickEventListenerSubscription();
      this.textInputEventListenerSubscription();
      this.contentKeyDownEventListenerSubscription();
      this.linkAddedSubscription();
   }

   /**
    *  ---- ----------------
    *  INITIALIZING SECTION
    *  --------------------
    */
   private contentClickEventListenerSubscription(): void {
      this._subscription.add(
         fromEvent(this.content.nativeElement, "click").subscribe((event: PointerEvent) => {
            if (this.editorService.isWrongSelection(event)) {
               this.resetEditorSelection();
               return;
            }

            this.editorService.setIsParameter(this.editorService.isParameter(event));
            this.setEditorOnSelection(window.getSelection());
         })
      );
   }

   private toolbarClickEventListenerSubscription(): void {
      this._subscription.add(
         fromEvent(this.toolbar.nativeElement, "click").subscribe((event: PointerEvent) => {
            if (this.editorService.hasClickedOnInputColorPicker(event)) {
               return;
            }
            this.editorService.restoreRangeSelection();
         })
      );
   }

   private textInputEventListenerSubscription(): void {
      const delayedInput$ = fromEvent(this.content.nativeElement, "textInput").pipe(throttleTime(500));

      this._subscription.add(
         delayedInput$.subscribe((result) => {
            const selection = this.editorService.getSelectedElement();
            this.saveHistory(
               selection.containerRef.innerHTML,
               selection.containerRef.innerHTML,
               selection.templateVariable
            );
         })
      );
   }

   private contentKeyDownEventListenerSubscription(): void {
      const { isArrowKeyPress, isShortcutPress } = this.editorService;

      this._subscription.add(
         fromEvent(this.content.nativeElement, "keydown").subscribe((res: CustomKeyboardEvent<Element>) => {
            // capturing Select ALL
            if (isShortcutPress("SELECT_ALL", res)) {
               if (window.getSelection().toString() === "") {
                  this.editorService.setIsSelectAll(true);
               }
               this.setEditorOnSelectionDelayed();
               return;
            }

            if (isArrowKeyPress(res)) {
               const selection = this.editorService.createSelectedElement();
               this.stylesService.analyzeAllAppliedStyles(selection);
               return;
            }

            if (isShortcutPress("UNDERLINE", res)) {
               this.onUnderline();
               const selection = this.editorService.createSelectedElement();
               this.stylesService.analyzeAllAppliedStyles(selection);
               res.stopPropagation();
               res.preventDefault();
               return;
            }

            if (isShortcutPress("BOLD", res)) {
               this.onBold();
               const selection = this.editorService.createSelectedElement();
               this.stylesService.analyzeAllAppliedStyles(selection);
               res.stopPropagation();
               res.preventDefault();
               return;
            }

            if (isShortcutPress("UNDO", res)) {
               this.undoChanges();
            }
         })
      );
   }

   private linkAddedSubscription(): void {
      this._subscription.add(
         this.linkToolService.onLinkModalClose$
            .pipe(map((link) => this.linkToolService.getAElementAsInnerHTML(link)))
            .subscribe((linkAsInnerHTML) => {
               const selectedElement = this.editorService.getSelectedElement();
               const previousValue = selectedElement.containerRef.innerHTML;

               this.editorService.restoreRangeSelection();
               this.editorService.insertInRangeSelection(linkAsInnerHTML);

               // saving history for Undo/Redo
               this.saveHistory(
                  selectedElement.containerRef.innerHTML,
                  previousValue,
                  selectedElement.templateVariable
               );
               // updating template model
               this.updateTemplateModel(selectedElement.containerRef.innerHTML, selectedElement.templateVariable);
            })
      );
   }

   private selectedStylesChangesSubscription(): void {
      this._subscription.add(
         this.selectedStylesSubject$.subscribe((fontStyles) => {
            if (fontStyles !== undefined) {
               this.isBold = fontStyles.isBold;
               this.isUnderline = fontStyles.isUnderLine;
               this.textAlignment = fontStyles.textAlignment;
               if (fontStyles.fontFamily === undefined) {
                  this.selectedFont = this.fontSource[0];
                  this.selectedFontSize = this.fontSize[0];
                  this.pickedColor = "#000000";
               } else {
                  const selectedFont = this.fontSource.find((font) => font.value === fontStyles.fontFamily);
                  this.selectedFont = selectedFont;
                  this.selectedFontSize = this.fontSize.find((font) => font.value === fontStyles.fontSize);
                  this.pickedColor = fontStyles.fontColor;
               }
            }
         })
      );
   }

   private getPresentAfterUndoRedoSubscription(): void {
      this._subscription.add(
         this.getPresentAfterUndoRedo$
            .pipe(filter((result) => Boolean(result)))
            .subscribe((present) => this.applyUndoRedo(present))
      );
   }

   private windowSelectionUpdateSubscription(): void {
      this._subscription.add(
         this.windowSelectionTimestamp$.subscribe((timestamp) => {
            const selection = window.getSelection();
            if (timestamp !== 0 && this.editorService.isNodeInEditorBody(selection.anchorNode)) {
               this.setEditorOnSelection(selection);
            }
         })
      );
   }

   // this is a hack to solve only after images are updates...
   // in order to ensure that image variable in model is not overwrite by old value
   private templateUpdateModelChangesSubscription(): void {
      this._subscription.add(
         this.previewEditTemplateSelector
            .selectTemplateUpdateModel()
            .pipe(filter((model) => model.updateType === "imageUpload"))
            .subscribe((model) => {
               this.templateUpdateModel = model;
            })
      );
   }

   /**
    *  END initializing
    */

   onBold() {
      console.log("------------  BOLD CHANGE ------------");
      const selectedElement = this.editorService.getSelectedElement();

      if (!Boolean(selectedElement)) {
         // selection has been reset or is null
         return;
      }

      const previousValue = selectedElement.containerRef.innerHTML;

      const elementsAsString = this.boldToolService.getStyledElementsAsString(selectedElement, {
         unBoldText: this.isBold,
      });

      console.log({ elementsAsString });

      this.boldToolService.insertElementAndSaveRangeSelection(elementsAsString, selectedElement.isParameter, {
         unBoldText: this.isBold,
      });

      // updating anchor node after insert new selection
      const selection = this.editorService.getWindowSelection();
      this.stylesService.analyzeAllAppliedStyles(selection);

      // saving history for Undo/Redo
      this.saveHistory(selectedElement.containerRef.innerHTML, previousValue, selectedElement.templateVariable);

      // updating template model
      this.updateTemplateModel(selectedElement.containerRef.innerHTML, selectedElement.templateVariable);
   }

   onUnderline() {
      console.log("------------  UNDERLINE CHANGE ------------");
      const selectedElement = this.editorService.getSelectedElement();

      if (!Boolean(selectedElement)) {
         // selection has been reset or is null
         return;
      }

      const previousValue = selectedElement.containerRef.innerHTML;

      const elementsAsString = this.underlineToolService.getStyledElementsAsString(selectedElement, {
         unUnderlineText: this.isUnderline,
      });

      console.log({ elementsAsString });

      this.underlineToolService.insertElementAndSaveRangeSelection(elementsAsString, selectedElement.isParameter, {
         unUnderlineText: this.isUnderline,
      });

      // updating anchor node after insert new selection
      const selection = this.editorService.getWindowSelection();
      this.stylesService.analyzeAllAppliedStyles(selection);

      // saving history for Undo/Redo
      this.saveHistory(selectedElement.containerRef.innerHTML, previousValue, selectedElement.templateVariable);

      // updating template model
      this.updateTemplateModel(selectedElement.containerRef.innerHTML, selectedElement.templateVariable);
   }

   undoChanges(): void {
      this.store.dispatch(EditorActions.undoChange());
   }

   redoChanges(): void {
      this.store.dispatch(EditorActions.redoChange());
   }

   resetChanges(): void {
      this.store.dispatch(EditorActions.resetHistory());
      this.store.dispatch(EditorActions.resetWindowSelectionCounter());
      this.clearDelayTimeOut(this._delayTimeOut);
   }

   onAlignmentChange(type: AlignmentType): void {
      console.log("------------  ALIGNMENT CHANGE ------------");
      const selectedElement = this.editorService.getSelectedElement();

      if (!Boolean(selectedElement)) {
         // selection has been reset or is null
         return;
      }

      const previousValue = selectedElement.containerRef.innerHTML;

      // generate string innerHTML from selection with  align rule applied
      const alignedElementsAsString = this.alignmentToolService.getStyledElementsAsString(selectedElement, {
         alignmentType: type,
      });

      // updating html
      this.alignmentToolService.insertElementAndSaveRangeSelection(
         alignedElementsAsString,
         selectedElement.isParameter
      );

      // updating anchor node after insert new selection
      const selection = this.editorService.getWindowSelection();
      this.stylesService.analyzeAllAppliedStyles(selection);

      // saving history for Undo/Redo
      this.saveHistory(selectedElement.containerRef.innerHTML, previousValue, selectedElement.templateVariable);

      // updating template model
      this.updateTemplateModel(selectedElement.containerRef.innerHTML, selectedElement.templateVariable);
   }

   onAddLink(): void {
      if (!Boolean(this.editorService.getSelectedElement())) {
         // selection has been reset or is null
         return;
      }

      this.editorService.openAddLinkDialog();
   }

   onFontChanges(changeType: FontChangeType): void {
      console.log("------------  NEW FONT CHANGE ------------");
      const selectedElement = this.editorService.getSelectedElement();

      if (!Boolean(selectedElement)) {
         // selection has been reset or is null
         return;
      }

      const previousValue = selectedElement.containerRef.innerHTML;
      let fontChangeAndValue: FontStyleArgument;

      switch (changeType) {
         case "FONT_COLOR":
            fontChangeAndValue = { property: "color", value: this.selectedFontColor };
            break;
         case "FONT_SIZE":
            fontChangeAndValue = { property: "font-size", value: this.selectedFontSize.value };
            break;
         case "FONT_FAMILY":
            fontChangeAndValue = { property: "font-family", value: this.selectedFont.value };
            break;
      }

      const elementsAsString = this.fontToolService.getStyledElementsAsString(selectedElement, {
         fontStyle: fontChangeAndValue,
      });

      console.log({ elementsAsString });

      this.fontToolService.insertElementAndSaveRangeSelection(elementsAsString, selectedElement.isParameter);

      // updating anchor node after insert new selection
      const selection = this.editorService.getWindowSelection();
      this.stylesService.analyzeAllAppliedStyles(selection);

      // saving history for Undo/Redo
      this.saveHistory(selectedElement.containerRef.innerHTML, previousValue, selectedElement.templateVariable);

      // updating template model
      this.updateTemplateModel(selectedElement.containerRef.innerHTML, selectedElement.templateVariable);
   }

   onFontColorChange(color: string) {
      if (!Boolean(this.editorService.getSelectedElement())) {
         // selection has been reset or is null
         return;
      }

      this.selectedFontColor = color;
      this.onFontChanges("FONT_COLOR");
   }

   getFontStyleByName(fontName: string): string {
      return `font-family: ${fontName}`;
   }

   getFontSizeByName(fontSize: string): string {
      return `font-size: ${fontSize}px`;
   }

   private saveHistory(currentValue: string, previousValue: string, updateModelVariable: string): void {
      if (!Boolean(previousValue) || !Boolean(updateModelVariable)) {
         return;
      }

      if (!Boolean(this._previousTemplateVariableId)) {
         this._previousTemplateVariableId = updateModelVariable;
      }

      if (this._previousTemplateVariableId !== updateModelVariable) {
         this.store.dispatch(
            EditorActions.updateHistoryForDifferentVariable({
               previousValue,
               currentValue,
               updateModelVariable,
            })
         );
      } else {
         this.store.dispatch(
            EditorActions.updateHistory({
               previousValue,
               currentValue,
               updateModelVariable,
            })
         );
      }
      this._previousTemplateVariableId = updateModelVariable;
   }

   private updateTemplateModel(innerHTML: string, variableId: string): void {
      const selectedElement = this.editorService.getSelectedElement();
      const dbModel = { ...this.templateUpdateModel.updatedModel };
      const model = selectedElement.containerRef.id;
      if (model !== undefined) {
         dbModel[model.toString().split(".")[1]] = this.contenteditableDirective.applyHtmlParsing(
            selectedElement.containerRef.innerHTML
         );
      }

      const templateUpdateModel = {
         updatedModel: dbModel,
         isModelUpdated: true,
         updateType: "model",
         type: "",
      };
      this.store.dispatch(PreviewEditTemplateActions.templateUpdateModel({ templateUpdateModel }));
   }

   private updateHTMLContent(templateVariableID: string, innerHTML: string): void {
      // preventing error by undefined values
      if (!Boolean(templateVariableID) || !Boolean(innerHTML)) {
         return;
      }

      const element = document.getElementById(`template.${templateVariableID}`);
      // preventing update same content
      if (element.innerHTML !== innerHTML) {
         element.innerHTML = innerHTML;
      }
   }

   private applyUndoRedo(contentAndModelId: ContentAndModelId): void {
      try {
         Object.keys(contentAndModelId).forEach((templateVariableId) => {
            const innerHTML = contentAndModelId[templateVariableId];
            this.updateHTMLContent(templateVariableId, innerHTML);
            this.updateTemplateModel(templateVariableId, innerHTML);
         });
      } catch (error) {
         this.editorService.logError(error, "Applying Undo Redo");
      }
   }

   private setEditorOnSelection(newSelection: Selection): void {
      const templateIdAndHtmlElement = this.editorService.getTemplateIdAndHtmlElement(newSelection);

      if (!this.editorService.getIsParameter()) {
         const correctedSelection = this.editorService.correctSelectionIfAnyIsNotText(newSelection);
         this.editorService.setWindowSelection(correctedSelection);
      } else {
         this.editorService.setWindowSelection(newSelection);
      }

      const { htmlElement } = templateIdAndHtmlElement;
      this.editorService.setNgModelValueAsIdToElement(htmlElement);

      const selectedElement = this.editorService.createSelectedElement(templateIdAndHtmlElement);
      this.stylesService.analyzeAllAppliedStyles(selectedElement);
   }

   private setEditorOnSelectionDelayed(): void {
      this.clearDelayTimeOut(this._delayTimeOut);
      this._delayTimeOut = setTimeout(() => {
         this.setEditorOnSelection(window.getSelection());
      }, 500);
   }

   private clearDelayTimeOut(timeout: Timeout): void {
      if (timeout) {
         clearTimeout(timeout);
      }
   }

   private resetEditorSelection(): void {
      this.editorService.initialize();
      this.selectedFont = this.fontSource[0];
      this.selectedFontSize = this.fontSize[0];
      this.pickedColor = "#000000";
      this.isBold = false;
      this.isUnderline = false;
   }
}
